
import { DentalArchSelector } from './DentalArchSelector';
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Upload, ArrowRight, ArrowLeft, Check, AlertTriangle, Eye, Layers, ChevronRight, CheckCircle, User as UserIcon, Calendar, FileText, Save, Clock, Image as ImageIcon, Trash2, List, AlertCircle, Cpu, Plus, Banknote, ShieldCheck, Info, MapPin, Download, Package, FolderOpen } from 'lucide-react';
import { Button, Input, Select, StatusBadge } from './ui';
import { User, Order, Urgency, ServiceCategory, ServiceItem, OrderItem, SystemSettings, UserRole, ClinicBranch, OrderFile } from '../types';
import { api } from '../services/api';
import { STLViewer } from './STLViewer';
import JSZip from 'jszip'; // Import JSZip

interface OrderModalProps {
   isOpen: boolean;
   onClose: () => void;
   onQueueUpload: (files: File[]) => void;
   onSubmit: (data: any, files: File[]) => Promise<Order | void>;
   user: User;
   initialOrder?: Order | null; // For editing
}

// ... DentalArchSelector ... (Same as existing)
// DentalArchSelector imported from ./DentalArchSelector

export const OrderModal = ({ isOpen, onClose, onSubmit, onQueueUpload, user, initialOrder }: OrderModalProps) => {
   const [step, setStep] = useState(1);
   const [loading, setLoading] = useState(false);
   const [services, setServices] = useState<ServiceCategory[]>([]);
   const [settings, setSettings] = useState<SystemSettings>({ id: 'default', pmma_price: 100, urgent_rate: 5, currency: 'EGP' });
   const [doctors, setDoctors] = useState<User[]>([]);
   const [branches, setBranches] = useState<ClinicBranch[]>([]);
   const [showValidation, setShowValidation] = useState(false);
   const [missingFields, setMissingFields] = useState<string[]>([]);

   const [formData, setFormData] = useState<Partial<Order>>({
      patientName: '',
      shade: '',
      urgency: Urgency.NORMAL,
      notes: '',
      items: [],
      toothNumber: '',
      isPMMA: false,
      requiresDesignCheck: false,
      files: [],
      doctorId: user.role === UserRole.DENTIST ? user.id : '',
      branchId: ''
   });

   const [selectedTeeth, setSelectedTeeth] = useState<string[]>([]);
   const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
   const [paymentProofFile, setPaymentProofFile] = useState<File | null>(null);

   // Archive Inspector State
   const [archiveFile, setArchiveFile] = useState<File | Blob | null>(null);
   const [archiveContent, setArchiveContent] = useState<string[]>([]);
   const [viewingSTL, setViewingSTL] = useState<string | null>(null); // URL of blob
   const [showArchiveModal, setShowArchiveModal] = useState(false);

   useEffect(() => {
      if (isOpen) {
         loadData(user.role === UserRole.DENTIST ? user.id : undefined);
         if (initialOrder) {
            setFormData({
               patientName: initialOrder.patientName,
               shade: initialOrder.shade,
               urgency: initialOrder.urgency,
               notes: initialOrder.notes,
               items: initialOrder.items,
               toothNumber: initialOrder.toothNumber,
               isPMMA: initialOrder.isPMMA,
               requiresDesignCheck: initialOrder.requiresDesignCheck,
               dueDate: initialOrder.dueDate,
               doctorId: initialOrder.doctorId,
               branchId: initialOrder.branchId || ''
            });
            const allTeeth = initialOrder.items.flatMap(i => i.teeth);
            setSelectedTeeth(allTeeth);
         } else {
            resetForm();
         }
      }
   }, [isOpen, initialOrder]);

   useEffect(() => {
      if ((user.role === UserRole.ADMIN || user.role === UserRole.MODERATOR) && formData.doctorId) {
         loadData(formData.doctorId);
      }
   }, [formData.doctorId]);

   // Recalculate prices when services change
   useEffect(() => {
      if (formData.items && formData.items.length > 0 && services.length > 0) {
         setFormData((prev: Partial<Order>) => {
            const updatedItems = prev.items?.map((item: OrderItem) => {
               const matchingService = services
                  .flatMap((cat: ServiceCategory) => cat.services)
                  .find((s: ServiceItem) => s.id === item.serviceId);

               if (matchingService) {
                  return {
                     ...item,
                     price: matchingService.price * (item.teeth.length || 1)
                  };
               }
               return item;
            });

            return {
               ...prev,
               items: updatedItems
            };
         });
      }
   }, [services]);

   const resetForm = () => {
      setFormData({
         patientName: '',
         shade: '',
         urgency: Urgency.NORMAL,
         notes: '',
         items: [],
         toothNumber: '',
         isPMMA: false,
         requiresDesignCheck: false,
         files: [],
         doctorId: user.role === UserRole.DENTIST ? user.id : '',
         branchId: ''
      });
      setSelectedTeeth([]);
      setFilesToUpload([]);
      setPaymentProofFile(null);
      setStep(1);
      setShowValidation(false);
      setMissingFields([]);
   };

   const loadData = async (targetUserId?: string) => {
      const [srvs, sets, usrs] = await Promise.all([
         api.getServices(targetUserId),
         api.getSettings(),
         user.role === UserRole.ADMIN || user.role === UserRole.MODERATOR ? api.getUsers() : Promise.resolve([])
      ]);
      setServices(srvs);
      setSettings(sets);
      if (usrs.length > 0) setDoctors(usrs.filter(u => u.role === UserRole.DENTIST));
      if (targetUserId) {
         api.getBranches(targetUserId).then(setBranches);
      } else {
         setBranches([]);
      }
   };

   const handleToothToggle = (id: string) => {
      setSelectedTeeth((prev: string[]) => prev.includes(id) ? prev.filter((t: string) => t !== id) : [...prev, id]);
   };

   const calculateTotal = () => {
      if (!formData.items) return 0;
      let total = formData.items.reduce((sum: number, item: OrderItem) => sum + item.price, 0);
      if (formData.isPMMA) {
         const totalTeethCount = formData.items.reduce((sum: number, item: OrderItem) => sum + item.teeth.length, 0);
         total += totalTeethCount * settings.pmma_price;
      }
      if (formData.urgency === Urgency.URGENT) {
         total += total * (settings.urgent_rate / 100);
      }
      return total;
   };

   const addItem = (service: ServiceItem, teethForService: string[]) => {
      const newItem: OrderItem = {
         serviceId: service.id,
         serviceName: service.name,
         teeth: teethForService,
         price: service.price * (teethForService.length || 1),
         details: ''
      };
      setFormData((prev: Partial<Order>) => ({
         ...prev,
         items: [...(prev.items || []), newItem]
      }));
      // NEW: Reset tooth selection after adding item
      setSelectedTeeth([]);
   };

   const removeItem = (index: number) => {
      setFormData((prev: Partial<Order>) => ({
         ...prev,
         items: prev.items?.filter((_: OrderItem, i: number) => i !== index)
      }));
   };

   const getNextDays = (days = 14) => {
      const list = [];
      for (let i = 0; i < days; i++) {
         const d = new Date();
         d.setDate(d.getDate() + i);
         list.push({
            value: d.toISOString().split('T')[0],
            label: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
         });
      }
      return list;
   };

   const validateStep = (currentStep: number) => {
      const missing: string[] = [];
      if (currentStep === 1) {
         if (!formData.patientName) missing.push('Patient Name');
         if (!formData.doctorId) missing.push('Doctor');
         if (!formData.dueDate && !initialOrder) missing.push('Due Date');
      }
      if (currentStep === 2) {
         if (user.role !== UserRole.ADMIN && (!formData.items || formData.items.length === 0)) missing.push('At least one service');
      }

      setMissingFields(missing);
      setShowValidation(missing.length > 0);
      return missing.length === 0;
   };

   const handleNext = () => {
      if (validateStep(step)) {
         setStep(step + 1);
         setShowValidation(false);
      }
   };

   const handleSubmit = async () => {
      setLoading(true);
      try {
         const totalCost = calculateTotal();
         const submissionData = {
            ...formData,
            price: totalCost,
            toothNumber: selectedTeeth.join(','),
         };
         const newOrder = await onSubmit(submissionData, filesToUpload);
         if (paymentProofFile && newOrder && newOrder.id) {
            await api.requestTopUp(totalCost, paymentProofFile, newOrder.id);
            alert("Order created and payment receipt submitted for approval.");
         }
      } catch (e: any) {
         console.error(e);
         alert("Failed to save order: " + (e.message || "Unknown error"));
      } finally {
         setLoading(false);
      }
   };

   // Archive Handling
   const handleInspectArchive = async (file: File | OrderFile) => {
      let blob: Blob;
      if ('url' in file) {
         const res = await fetch(file.url);
         blob = await res.blob();
      } else {
         blob = file;
      }
      setArchiveFile(blob);
      setViewingSTL(null);

      try {
         const zip = await JSZip.loadAsync(blob);
         const files: string[] = [];
         zip.forEach((relativePath) => files.push(relativePath));
         setArchiveContent(files);
         setShowArchiveModal(true);
      } catch (e) {
         alert("Could not open archive. Ensure it is a valid ZIP file. RAR files are not supported in browser preview.");
      }
   };

   const handleViewArchivedSTL = async (filename: string) => {
      if (!archiveFile) return;
      try {
         const zip = await JSZip.loadAsync(archiveFile);
         const file = zip.file(filename);
         if (file) {
            const blob = await file.async('blob');
            const url = URL.createObjectURL(blob);
            setViewingSTL(url);
         }
      } catch (e) {
         console.error(e);
      }
   };

   if (!isOpen) return null;

   const totalCost = calculateTotal();
   const canPayInstantly = user.balance >= totalCost;

   return ReactDOM.createPortal(
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 animate-fade-in">
         <div className="bg-surface border border-border w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh] h-full md:h-auto relative">

            {/* Archive Inspector Modal Overlay */}
            {showArchiveModal && (
               <div className="absolute inset-0 z-50 bg-background/95 flex flex-col p-6 animate-fade-in">
                  <div className="flex justify-between items-center mb-4">
                     <h3 className="text-xl font-bold text-primary flex items-center gap-2"><FolderOpen size={20} /> Archive Contents</h3>
                     <button onClick={() => setShowArchiveModal(false)} className="text-secondary hover:text-primary"><X size={24} /></button>
                  </div>
                  <div className="flex-1 flex gap-4 overflow-hidden">
                     <div className="w-1/3 bg-surface border border-border rounded-xl overflow-y-auto p-2">
                        {archiveContent.map(f => (
                           <div key={f} className="p-2 hover:bg-surfaceHighlight rounded cursor-pointer flex justify-between items-center group">
                              <span className="text-sm text-secondary truncate w-3/4">{f}</span>
                              {f.toLowerCase().endsWith('.stl') && (
                                 <button onClick={() => handleViewArchivedSTL(f)} className="p-1 bg-blue-600 rounded text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity">View</button>
                              )}
                           </div>
                        ))}
                     </div>
                     <div className="flex-1 bg-black rounded-xl border border-zinc-800 relative overflow-hidden">
                        {viewingSTL ? <STLViewer url={viewingSTL} /> : <div className="absolute inset-0 flex items-center justify-center text-zinc-500">Select an STL to view</div>}
                     </div>
                  </div>
               </div>
            )}

            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-border bg-background">
               <div>
                  <h2 className="text-xl font-bold text-primary flex items-center gap-2">
                     {initialOrder ? 'Edit Order' : 'New Case Request'}
                     <span className="text-sm font-normal text-secondary bg-surface px-2 py-0.5 rounded border border-border">Step {step} of 4</span>
                  </h2>
                  <p className="text-sm text-secondary hidden md:block">
                     {step === 1 ? "Patient & General Info" : step === 2 ? "Technical Details" : step === 3 ? "Upload Files" : "Financials & Payment"}
                  </p>
               </div>
               <button onClick={onClose} className="p-2 text-secondary hover:text-primary rounded-full hover:bg-surfaceHighlight transition-colors">
                  <X size={24} />
               </button>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-surfaceHighlight h-1">
               <div className="h-full bg-blue-500 transition-all duration-300" style={{ width: `${(step / 4) * 100}%` }}></div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 bg-surface custom-scrollbar">

               {/* STEP 1: GENERAL INFO (Same as before) */}
               {step === 1 && (
                  <div className="space-y-6 animate-slide-up">
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Input label="Patient Name" value={formData.patientName} onChange={(e: any) => setFormData({ ...formData, patientName: e.target.value })} placeholder="Full Name" error={showValidation && !formData.patientName} autoFocus />

                        {(user.role === UserRole.ADMIN || user.role === UserRole.MODERATOR) && (
                           <div>
                              <label className="block text-xs font-bold text-zinc-400 uppercase mb-2">Doctor</label>
                              <select
                                 className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3.5 text-white outline-none focus:border-blue-500"
                                 value={formData.doctorId}
                                 onChange={(e) => setFormData({ ...formData, doctorId: e.target.value })}
                              >
                                 <option value="">Select Doctor</option>
                                 {doctors.map(d => <option key={d.id} value={d.id}>{d.name} ({d.clinicName})</option>)}
                              </select>
                           </div>
                        )}

                        {branches.length > 0 && (
                           <div>
                              <label className="block text-xs font-bold text-secondary uppercase mb-2">Clinic Branch</label>
                              <div className="relative">
                                 <select
                                    className="w-full bg-surfaceHighlight border border-border rounded-xl px-4 py-3.5 text-primary outline-none focus:border-blue-500 appearance-none"
                                    value={formData.branchId || ''}
                                    onChange={(e) => setFormData({ ...formData, branchId: e.target.value })}
                                 >
                                    <option value="">Main Clinic / Default</option>
                                    {branches.map(b => <option key={b.id} value={b.id}>{b.name} - {b.location ? '📍' : ''}</option>)}
                                 </select>
                                 <div className="absolute right-4 top-4 pointer-events-none text-secondary"><MapPin size={16} /></div>
                              </div>
                           </div>
                        )}

                        <div className="col-span-1 md:col-span-2">
                           <label className="block text-xs font-bold text-secondary uppercase mb-2">Due Date</label>
                           <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                              {getNextDays(7).map((day) => (
                                 <button
                                    key={day.value}
                                    onClick={() => setFormData({ ...formData, dueDate: day.value })}
                                    className={`p-3 rounded-xl border text-center transition-all ${formData.dueDate === day.value ? 'bg-blue-600 border-blue-500 text-white' : 'bg-surfaceHighlight border-border text-secondary hover:border-zinc-500'}`}
                                 >
                                    <div className="text-xs font-bold mb-1">{day.label.split(',')[0]}</div>
                                    <div className="text-[10px] opacity-70">{day.value.split('-').slice(1).join('/')}</div>
                                 </button>
                              ))}
                              <div className="relative col-span-1">
                                 <input type="date" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })} />
                                 <div className={`h-full w-full flex flex-col items-center justify-center p-3 rounded-xl border bg-surfaceHighlight border-border text-secondary hover:border-zinc-500`}>
                                    <Calendar size={16} className="mb-1" />
                                    <span className="text-[10px]">More</span>
                                 </div>
                              </div>
                           </div>
                        </div>

                        <div><Input label="Shade" value={formData.shade} onChange={(e: any) => setFormData({ ...formData, shade: e.target.value })} placeholder="e.g. A2, 3M2" /></div>

                        <div>
                           <label className="block text-xs font-bold text-secondary uppercase mb-2">Urgency</label>
                           <div className="flex bg-surfaceHighlight rounded-xl p-1 border border-border">
                              <button onClick={() => setFormData({ ...formData, urgency: Urgency.NORMAL })} className={`flex-1 py-3 rounded-lg text-sm font-medium transition-all ${formData.urgency === Urgency.NORMAL ? 'bg-surface text-primary shadow-sm' : 'text-secondary hover:text-primary'}`}>Normal</button>
                              <button onClick={() => setFormData({ ...formData, urgency: Urgency.URGENT })} className={`flex-1 py-3 rounded-lg text-sm font-medium transition-all ${formData.urgency === Urgency.URGENT ? 'bg-red-900/20 text-red-400 border border-red-900/30' : 'text-secondary hover:text-primary'}`}>Urgent (+{settings.urgent_rate}%)</button>
                           </div>
                        </div>
                     </div>

                     <div>
                        <label className="block text-xs font-bold text-secondary uppercase mb-2">Notes & Instructions</label>
                        <textarea className="w-full bg-surfaceHighlight border border-border rounded-xl p-4 text-primary outline-none focus:border-blue-500 min-h-[120px]" placeholder="Describe the case requirements..." value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })}></textarea>
                     </div>
                  </div>
               )}

               {/* STEP 2: TECHNICAL (Same as before) */}
               {step === 2 && (
                  <div className="space-y-6 animate-slide-up">
                     <DentalArchSelector
                        selectedTeeth={selectedTeeth}
                        onToggleTooth={handleToothToggle}
                        toothColors={
                           formData.items?.reduce((acc, item) => {
                              const srv = services.flatMap(c => c.services).find(s => s.id === item.serviceId);
                              if (srv) {
                                 item.teeth.forEach(t => {
                                    acc[t] = { color: srv.color || '#3b82f6', label: item.serviceName };
                                 });
                              }
                              return acc;
                           }, {} as Record<string, { color: string, label: string }>)
                        }
                     />

                     <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-3">
                           <h3 className="font-bold text-white text-sm uppercase tracking-wider">Select Restoration</h3>
                           <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                              {services.map(cat => (
                                 <div key={cat.id} className="bg-zinc-950 rounded-xl border border-zinc-800 overflow-hidden">
                                    <div className="px-4 py-2 bg-zinc-900/50 text-[10px] font-bold text-zinc-500 uppercase">{cat.name}</div>
                                    <div className="divide-y divide-zinc-800">
                                       {cat.services.map(srv => (
                                          <button key={srv.id} onClick={() => addItem(srv, selectedTeeth)} className="w-full flex justify-between items-center px-4 py-3 hover:bg-zinc-800 transition-colors text-left group">
                                             <div className="flex items-center gap-2">
                                                <div className="w-3 h-3 rounded-full shadow-sm shrink-0" style={{ backgroundColor: srv.color || '#3b82f6' }}></div>
                                                <span className="text-sm text-zinc-300 group-hover:text-white">{srv.name}</span>
                                             </div>
                                             <div className="flex items-center gap-2">
                                                <span className="text-xs text-zinc-500 font-mono">{srv.price}</span>
                                                <Plus size={16} className="text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                                             </div>
                                          </button>
                                       ))}
                                    </div>
                                 </div>
                              ))}
                           </div>
                        </div>

                        <div className="space-y-3">
                           <h3 className="font-bold text-white text-sm uppercase tracking-wider">Case Items</h3>
                           <div className="bg-zinc-950 rounded-xl border border-zinc-800 min-h-[200px] p-2 space-y-2">
                              {(formData.items || []).map((item, idx) => (
                                 <div key={idx} className="bg-zinc-900 p-3 rounded-lg border border-zinc-800 flex justify-between items-start group relative">
                                    <div>
                                       <div className="font-bold text-white text-sm">{item.serviceName}</div>
                                       <div className="text-xs text-zinc-500 mt-1">Teeth: {item.teeth.length > 0 ? item.teeth.join(', ') : 'None selected'}</div>
                                       <input className="mt-2 bg-black/30 border border-zinc-800 rounded px-2 py-1 text-xs text-white w-full placeholder-zinc-600 focus:border-blue-500 outline-none" placeholder="Specific details (e.g. Implant brand)" value={item.details || ''} onChange={(e) => {
                                          const newItems = [...(formData.items || [])];
                                          newItems[idx].details = e.target.value;
                                          setFormData({ ...formData, items: newItems });
                                       }}
                                       />
                                    </div>
                                    <div className="text-right">
                                       <div className="font-mono text-white text-sm">{item.price}</div>
                                       <button onClick={() => removeItem(idx)} className="text-zinc-600 hover:text-red-400 mt-2 p-1 absolute top-1 right-1"><Trash2 size={14} /></button>
                                    </div>
                                 </div>
                              ))}
                              {(!formData.items || formData.items.length === 0) && <div className="text-center text-zinc-600 py-10 text-sm flex flex-col items-center gap-2"><Cpu size={24} className="opacity-50" /> Select teeth and a service to add items.</div>}
                           </div>
                           <div className="space-y-2">
                              <label className="flex items-center gap-3 p-3 bg-zinc-950 border border-zinc-800 rounded-xl cursor-pointer hover:border-zinc-700 transition-colors"><input type="checkbox" checked={formData.requiresDesignCheck} onChange={(e) => setFormData({ ...formData, requiresDesignCheck: e.target.checked })} className="w-4 h-4 rounded bg-zinc-900 border-zinc-600 checked:bg-blue-500" /><span className="text-sm text-zinc-300">Request Design Approval</span></label>
                              <label className="flex items-center gap-3 p-3 bg-zinc-950 border border-zinc-800 rounded-xl cursor-pointer hover:border-zinc-700 transition-colors"><input type="checkbox" checked={formData.isPMMA} onChange={(e) => setFormData({ ...formData, isPMMA: e.target.checked })} className="w-4 h-4 rounded bg-zinc-900 border-zinc-600 checked:bg-purple-500" /><div><span className="text-sm text-zinc-300 block">PMMA Try-In Required</span><span className="text-zinc-500 text-[10px]">Adds cost per tooth</span></div></label>
                           </div>
                        </div>
                     </div>
                  </div>
               )}

               {/* STEP 3: FILES (Updated with Archive Logic) */}
               {step === 3 && (
                  <div className="space-y-6 animate-slide-up h-full flex flex-col">
                     <div className="border-2 border-dashed border-zinc-800 rounded-2xl p-8 text-center hover:bg-zinc-800/30 transition-colors relative flex-1 flex flex-col items-center justify-center">
                        <input type="file" multiple className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" onChange={(e) => { if (e.target.files) setFilesToUpload([...filesToUpload, ...Array.from(e.target.files)]); }} />
                        <div className="pointer-events-none">
                           <div className="w-16 h-16 bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-4 text-blue-400"><Upload size={32} /></div>
                           <p className="text-white font-bold mb-2 text-lg">Drag & Drop Scans</p>
                           <p className="text-zinc-500 text-sm">Supported: STL, PLY, OBJ, ZIP, RAR</p>
                        </div>
                     </div>

                     {filesToUpload.length > 0 && (
                        <div className="space-y-2">
                           <h4 className="text-xs font-bold text-zinc-500 uppercase">Queue</h4>
                           {filesToUpload.map((file, idx) => (
                              <div key={idx} className="flex items-center justify-between p-3 bg-zinc-950 border border-zinc-800 rounded-xl animate-fade-in">
                                 <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-zinc-900 rounded flex items-center justify-center text-zinc-500"><FileText size={16} /></div>
                                    <div>
                                       <p className="text-sm font-medium text-white truncate max-w-[200px]">{file.name}</p>
                                       <p className="text-xs text-zinc-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                                    </div>
                                 </div>
                                 <div className="flex gap-2">
                                    {file.name.toLowerCase().endsWith('.zip') && (
                                       <button onClick={() => handleInspectArchive(file)} className="p-2 text-blue-400 hover:bg-blue-900/20 rounded"><FolderOpen size={16} /></button>
                                    )}
                                    <button onClick={() => setFilesToUpload(filesToUpload.filter((_, i) => i !== idx))} className="text-zinc-600 hover:text-red-400 p-2"><X size={16} /></button>
                                 </div>
                              </div>
                           ))}
                        </div>
                     )}

                     {/* Existing Files on Order (for edit mode) */}
                     {initialOrder && initialOrder.files.length > 0 && (
                        <div className="space-y-2 pt-4 border-t border-zinc-800">
                           <h4 className="text-xs font-bold text-zinc-500 uppercase">Uploaded</h4>
                           {initialOrder.files.map(f => (
                              <div key={f.id} className="flex justify-between items-center p-3 bg-zinc-950/50 rounded-xl border border-zinc-800">
                                 <span className="text-sm text-zinc-400">{f.name}</span>
                                 {f.name.toLowerCase().endsWith('.zip') && (
                                    <button onClick={() => handleInspectArchive(f)} className="text-xs bg-blue-900/30 text-blue-400 px-2 py-1 rounded hover:bg-blue-900/50 flex items-center gap-1"><FolderOpen size={12} /> Inspect</button>
                                 )}
                              </div>
                           ))}
                        </div>
                     )}
                  </div>
               )}

               {/* STEP 4: FINANCIALS (Same as before) */}
               {step === 4 && (
                  <div className="space-y-6 animate-slide-up">
                     <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 flex gap-3">
                        <Info className="text-blue-400 shrink-0" size={20} />
                        <div>
                           <h4 className="text-sm font-bold text-white mb-1">Payment Information</h4>
                           <p className="text-xs text-zinc-400">
                              You can settle the payment now or wait until the design is ready.
                              <span className="block mt-1 text-blue-200">Note: Payment is required before the milling phase begins.</span>
                           </p>
                        </div>
                     </div>

                     <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-6">
                        <h3 className="text-lg font-bold text-white mb-4">Order Summary</h3>
                        <div className="space-y-3 mb-6">
                           {formData.items?.map((item, i) => (
                              <div key={i} className="flex justify-between text-sm">
                                 <span className="text-zinc-400">{item.serviceName} x{item.teeth.length || 1}</span>
                                 <span className="text-zinc-200">{item.price}</span>
                              </div>
                           ))}
                           {formData.isPMMA && (
                              <div className="flex justify-between text-sm">
                                 <span className="text-purple-400">PMMA Try-In</span>
                                 <span className="text-zinc-200">{(formData.items?.reduce((s, i) => s + i.teeth.length, 0) || 0) * settings.pmma_price}</span>
                              </div>
                           )}
                           {formData.urgency === Urgency.URGENT && (
                              <div className="flex justify-between text-sm">
                                 <span className="text-red-400">Urgency Fee (+{settings.urgent_rate}%)</span>
                                 <span className="text-zinc-200">{(calculateTotal() * (settings.urgent_rate / (100 + settings.urgent_rate))).toFixed(0)}</span>
                              </div>
                           )}
                        </div>
                        <div className="border-t border-zinc-800 pt-4 flex justify-between items-center">
                           <span className="text-zinc-500 font-bold uppercase text-xs">Total Due</span>
                           <span className="text-3xl font-bold text-white">{calculateTotal().toLocaleString()} <span className="text-lg text-zinc-600">{settings.currency}</span></span>
                        </div>
                     </div>

                     <div className={`rounded-2xl p-6 border ${paymentProofFile ? 'bg-green-900/10 border-green-500/30' : 'bg-zinc-950 border-zinc-800'}`}>
                        <div className="flex items-center gap-3 mb-4">
                           <div className={`w-10 h-10 rounded-full flex items-center justify-center ${paymentProofFile ? 'bg-green-500/20 text-green-400' : 'bg-zinc-800 text-zinc-400'}`}><Banknote size={20} /></div>
                           <div><h3 className="font-bold text-white">Payment Method</h3><p className="text-xs text-zinc-400">{canPayInstantly ? 'Wallet balance available. Amount will be deducted upon approval.' : 'Insufficient balance. You can upload a receipt now or pay later.'}</p></div>
                        </div>
                        {!canPayInstantly && (
                           <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                              <div className="text-sm space-y-2 text-zinc-300"><p>Transfer via <strong>Instapay</strong>:</p><div className="bg-black/30 p-3 rounded-lg font-mono text-lg text-white select-all cursor-pointer border border-white/5 hover:border-green-500/50 transition-colors">01556761102</div><p className="text-xs text-zinc-500">Account: Galal Dental Lab</p></div>
                              <div className="relative"><input type="file" accept="image/*,.pdf" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" onChange={(e) => setPaymentProofFile(e.target.files?.[0] || null)} /><div className={`h-full border-2 border-dashed rounded-xl flex flex-col items-center justify-center p-4 text-center transition-all ${paymentProofFile ? 'border-green-500 bg-green-500/10' : 'border-zinc-700 hover:border-zinc-500 hover:bg-zinc-800/50'}`}>{paymentProofFile ? (<><CheckCircle size={24} className="text-green-500 mb-2" /><span className="text-sm font-bold text-white truncate w-full">{paymentProofFile.name}</span><span className="text-xs text-green-400">Attached</span></>) : (<><Upload size={24} className="text-zinc-500 mb-2" /><span className="text-sm text-zinc-300 font-bold">Upload Payment Receipt</span><span className="text-xs text-zinc-500">Click to browse (Optional)</span></>)}</div></div>
                           </div>
                        )}
                        {canPayInstantly && !paymentProofFile && (<div className="flex items-center gap-2 p-3 bg-green-900/10 border border-green-500/20 rounded-lg text-green-400 text-sm"><CheckCircle size={16} /> Will be deducted from wallet automatically upon acceptance.</div>)}
                     </div>
                  </div>
               )}

            </div>

            {/* Footer Navigation */}
            <div className="p-6 border-t border-zinc-800 bg-zinc-950 flex justify-between items-center">
               <div className="text-zinc-500 text-sm">{showValidation && missingFields.length > 0 && (<span className="text-red-400 flex items-center gap-2 animate-pulse"><AlertCircle size={14} /> Missing: {missingFields.join(', ')}</span>)}</div>
               <div className="flex gap-3">
                  {step > 1 ? <Button variant="secondary" onClick={() => setStep(step - 1)}><ArrowLeft size={16} /> Back</Button> : <Button variant="secondary" onClick={onClose}>Cancel</Button>}
                  {step < 4 ? <Button onClick={handleNext}>Next Step <ArrowRight size={16} /></Button> : <Button onClick={handleSubmit} disabled={loading} className="bg-green-600 hover:bg-green-700 border-none text-white shadow-lg shadow-green-900/20">{loading ? <Clock className="animate-spin" size={18} /> : <CheckCircle size={18} />}{loading ? 'Submitting...' : 'Submit Order'}</Button>}
               </div>
            </div>
         </div>
      </div>,
      document.body
   );
};
